import React, { useEffect, useState } from "react";
import { Admins } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const AdminsManager = () => {
  const { user } = useAuth();
  const [admins, setAdminsList] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setAdminsList(await Admins.list()); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { toast.error("Enter a valid email"); return; }
    setAdding(true);
    try {
      await Admins.add(email.trim().toLowerCase());
      setEmail("");
      toast.success("Admin added");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Add failed");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Remove admin ${a.email}?`)) return;
    try {
      await Admins.remove(a.email);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed");
    }
  };

  const isSuper = user?.is_super_admin;

  return (
    <div className="p-10" data-testid="admins-manager">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.22em] text-atc-blue mb-2">Team</div>
        <h1 className="font-display font-black text-4xl text-white">Admins</h1>
        <p className="text-slate-400 text-sm mt-2">
          {isSuper
            ? "Add or remove people who can sign in to the admin dashboard."
            : "Only the super-admin can add or remove admins."}
        </p>
      </div>

      {isSuper && (
        <form onSubmit={add} className="glass rounded-2xl p-5 mb-8 flex flex-col sm:flex-row gap-3">
          <Input
            type="email"
            placeholder="new-admin@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-slate-950 border-white/10 text-white"
            data-testid="new-admin-email"
          />
          <button
            type="submit"
            disabled={adding}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full brand-gradient text-white text-sm font-semibold disabled:opacity-60"
            data-testid="add-admin-button"
          >
            {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Add admin
          </button>
        </form>
      )}

      {loading ? (
        <div className="text-slate-400">Loading…</div>
      ) : (
        <div className="glass rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/60 text-slate-400 text-xs uppercase tracking-[0.16em]">
              <tr>
                <th className="text-left px-5 py-3">Email</th>
                <th className="text-left px-5 py-3">Role</th>
                <th className="text-left px-5 py-3">Added</th>
                <th className="text-right px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {admins.map((a) => (
                <tr key={a.email} data-testid={`admin-row-${a.email}`}>
                  <td className="px-5 py-4 text-white">{a.email}</td>
                  <td className="px-5 py-4">
                    {a.is_super_admin ? (
                      <span className="inline-flex items-center gap-1 text-atc-green text-xs uppercase tracking-wider">
                        <ShieldCheck className="h-3.5 w-3.5" /> Super admin
                      </span>
                    ) : (
                      <span className="text-slate-400 text-xs uppercase tracking-wider">Admin</span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-xs text-slate-500">
                    {a.added_at ? new Date(a.added_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-5 py-4 text-right">
                    {isSuper && !a.is_super_admin && (
                      <button onClick={() => remove(a)} className="text-slate-400 hover:text-rose-400" data-testid={`remove-${a.email}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminsManager;
